export type BrandColors = { primary: string; primaryHover: string; primarySoft: string };
export type BrandColorKey = keyof BrandColors;
export const BRAND_COLOR_LABELS: Record<BrandColorKey, string> = {
  primary: 'Primary color', primaryHover: 'Hover color', primarySoft: 'Soft background color',
};
export const isHexColor = (value: string) => /^#[0-9a-f]{6}$/i.test(value);
function channels(hex: string) { return [1, 3, 5].map((offset) => parseInt(hex.slice(offset, offset + 2), 16)); }
function luminance(hex: string) {
  const rgb = channels(hex).map((channel) => {
    const value = channel / 255;
    return value <= .04045 ? value / 12.92 : ((value + .055) / 1.055) ** 2.4;
  });
  return .2126 * rgb[0]! + .7152 * rgb[1]! + .0722 * rgb[2]!;
}
export function colorContrast(a: string, b: string) {
  const left = luminance(a), right = luminance(b);
  return (Math.max(left, right) + .05) / (Math.min(left, right) + .05);
}
export const contrastLabel = (ratio: number) => `${(Math.floor(ratio * 100) / 100).toFixed(2)}:1`;

/** A restrained 10% primary tint, lightened only as far as readable primary
 * text requires. Invalid typed primary values keep validation on that field. */
export function derivedBrandSoft(primary: string): string {
  if (!isHexColor(primary)) return '#FFFFFF';
  const tint = '#' + channels(primary).map((channel) => Math.round(channel * .1 + 255 * .9).toString(16).padStart(2, '0')).join('').toUpperCase();
  return readableBrandShade('primarySoft', tint, { primary, primaryHover: primary, primarySoft: tint }) ?? '#FFFFFF';
}

/** Same three comparisons as the server; do not round a failing ratio into a pass. */
export function brandColorErrors(colors: BrandColors): Partial<Record<BrandColorKey, string>> {
  const errors: Partial<Record<BrandColorKey, string>> = {};
  for (const key of Object.keys(BRAND_COLOR_LABELS) as BrandColorKey[]) {
    if (!isHexColor(colors[key])) errors[key] = `${BRAND_COLOR_LABELS[key]}: enter a six-digit hex color, such as #1652FF.`;
  }
  for (const key of ['primary', 'primaryHover'] as const) {
    if (!errors[key] && colorContrast(colors[key], '#FFFFFF') < 4.5) {
      errors[key] = `${BRAND_COLOR_LABELS[key]}: white text is hard to read (contrast ${contrastLabel(colorContrast(colors[key], '#FFFFFF'))}; minimum 4.5:1). Choose a darker color.`;
    }
  }
  if (isHexColor(colors.primary) && isHexColor(colors.primarySoft) && colorContrast(colors.primary, colors.primarySoft) < 4.5) {
    errors.primarySoft = `Soft background color: primary-colored text is hard to read (contrast ${contrastLabel(colorContrast(colors.primary, colors.primarySoft))}; minimum 4.5:1). Choose a lighter background or a darker primary color.`;
  }
  return errors;
}

/** Native pickers cannot mask their palette. Project the choice to the nearest
 * passing shade along its black/white tint path, without changing other fields. */
export function readableBrandShade(key: BrandColorKey, requested: string, colors: BrandColors): string | null {
  if (!isHexColor(requested)) return null;
  const passes = (value: string) => key === 'primarySoft'
    ? isHexColor(colors.primary) && colorContrast(value, colors.primary) >= 4.5
    : colorContrast(value, '#FFFFFF') >= 4.5 && (key !== 'primary'
      || (isHexColor(colors.primarySoft) && colorContrast(value, colors.primarySoft) >= 4.5));
  const normalized = requested.toUpperCase();
  if (passes(normalized)) return normalized;
  const rgb = channels(normalized), target = key === 'primarySoft' ? 255 : 0;
  for (let step = 1; step <= 255; step++) {
    const value = '#' + rgb.map((channel) => Math.round(channel + (target - channel) * step / 255).toString(16).padStart(2, '0')).join('').toUpperCase();
    if (passes(value)) return value;
  }
  return null; // A manually typed incompatible companion field must be fixed first.
}
